import { Controller } from "@hotwired/stimulus"

export const decode = (value) => Uint8Array.from(atob(value.replace(/-/g, "+").replace(/_/g, "/")), c => c.charCodeAt(0))
export const encode = (value) => btoa(String.fromCharCode(...new Uint8Array(value))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")

export function creationOptions(options) {
  return { ...options, challenge: decode(options.challenge), user: { ...options.user, id: decode(options.user.id) },
    excludeCredentials: (options.excludeCredentials || []).map(c => ({ ...c, id: decode(c.id) })) }
}

export function requestOptions(options) {
  return { ...options, challenge: decode(options.challenge),
    allowCredentials: (options.allowCredentials || []).map(c => ({ ...c, id: decode(c.id) })) }
}

export function serialize(credential) {
  const response = { clientDataJSON: encode(credential.response.clientDataJSON) }
  for (const key of ["attestationObject", "authenticatorData", "signature", "userHandle"]) {
    if (credential.response[key]) response[key] = encode(credential.response[key])
  }
  return { id: credential.id, rawId: encode(credential.rawId), type: credential.type, response,
    clientExtensionResults: credential.getClientExtensionResults() }
}

export default class extends Controller {
  static targets = ["message", "button", "password", "name", "cancel"]
  static values = { optionsUrl: String, submitUrl: String, mode: String }

  connect() {
    this.connected = true
    this.busy = false
    this.supported = !!(window.isSecureContext && window.PublicKeyCredential && navigator.credentials)
    this.buttonTarget.disabled = !this.supported
    if (!this.supported) {
      this.messageTarget.textContent = "Passkeys need a supported browser and a secure connection. You can still use your password."
    }
    // Start passkey requests only from the button: automatic conditional prompts
    // can fail to complete with iPhone password managers.
  }

  disconnect() {
    this.connected = false
    this.abortController?.abort()
  }

  cancel() {
    this.abortController?.abort()
  }

  async start(event) {
    event.preventDefault()
    if (!this.supported || this.busy) return
    this.busy = true
    this.buttonTarget.disabled = true
    this.messageTarget.textContent = this.modeValue === "register"
      ? "Follow your device’s instructions to add a passkey."
      : "Choose your passkey in your browser or password manager. If no prompt appears, cancel and try again."
    if (this.hasCancelTarget) this.cancelTarget.hidden = false
    this.abortController = new AbortController()
    try {
      if (!this.connected || this.abortController.signal.aborted) {
        throw new DOMException("Request cancelled", "AbortError")
      }
      const registering = this.modeValue === "register"
      const options = await this.post(this.optionsUrlValue, registering ? { password: this.passwordTarget.value } : {})
      const credential = registering
        ? await navigator.credentials.create({ publicKey: creationOptions(options), signal: this.abortController.signal })
        : await navigator.credentials.get({ publicKey: requestOptions(options), signal: this.abortController.signal })
      if (!credential) throw new Error("No passkey was selected. Please try again or use your password.")
      const result = await this.post(this.submitUrlValue, {
        credential: serialize(credential), ...(registering ? { name: this.nameTarget.value } : {})
      })
      if (this.hasPasswordTarget) this.passwordTarget.value = ""
      window.location.assign(result.redirect_to)
    } catch (error) {
      this.messageTarget.textContent = ["NotAllowedError", "AbortError"].includes(error.name)
        ? "Passkey request cancelled or timed out. You can try again or use your password."
        : error.name === "InvalidStateError" ? "This passkey is already registered. Try another passkey."
          : error.message
    } finally {
      this.busy = false
      this.buttonTarget.disabled = !this.supported
      if (this.hasCancelTarget) this.cancelTarget.hidden = true
    }
  }

  async post(url, body, signal = this.abortController?.signal) {
    const response = await fetch(url, { method: "POST", credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content },
      body: JSON.stringify(body), signal })
    if (response.redirected) {
      window.location.assign(response.url)
      throw new Error("Please complete your account setup to continue.")
    }
    const result = await response.json()
    if (!response.ok) throw new Error(result.error || "Could not complete the passkey request. Please try again.")
    return result
  }
}
